/**
 * ServiceCatalogPage - displays the service catalog with search and filtering
 * Route: /service-catalog
 */
import { useState, useMemo, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Server, Plus, Upload } from 'lucide-react'
import { PageShell } from '@/components/common/PageShell'
import { PageHeader } from '@/components/common/PageHeader'
import { EmptyState } from '@/components/common/EmptyState'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { DataTablePagination } from '@/components/data-display/DataTablePagination'
import {
  ServiceCatalogTable,
  ServiceDetailDialog,
  ServiceCatalogFilters,
} from '@/components/service-catalog'
import { ServiceFormDialog } from '@/components/service-catalog/ServiceFormDialog'
import { BulkUploadDialog } from '@/components/service-catalog/BulkUploadDialog'
import { useServiceCatalog } from '@/api/service-catalog'
import { usePermissions } from '@/hooks/usePermissions'
import type { ServiceSummary } from '@/lib/types'

const PAGE_SIZES = [10, 25, 50, 100]

// Debounce hook for search
function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState(value)

  useMemo(() => {
    const timer = setTimeout(() => setDebouncedValue(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])

  return debouncedValue
}

/**
 * Service Catalog page for viewing all services in the organization.
 * Provides search and filter capabilities with a detail drawer for each service.
 */
export function ServiceCatalogPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  
  // Parse URL params with defaults
  const page = parseInt(searchParams.get('page') ?? '1', 10)
  const pageSize = parseInt(searchParams.get('pageSize') ?? '25', 10)
  
  // Filter state
  const [search, setSearch] = useState(searchParams.get('search') ?? '')
  const [areaPath, setAreaPath] = useState(searchParams.get('areaPath') ?? '')
  const [ownerTeam, setOwnerTeam] = useState(searchParams.get('ownerTeam') ?? '')
  const [country, setCountry] = useState(searchParams.get('country') ?? '')
  
  // Selected service for detail drawer
  const [selectedService, setSelectedService] = useState<string | null>(null)

  // Admin dialog state
  const [showAddDialog, setShowAddDialog] = useState(false)
  const [showUploadDialog, setShowUploadDialog] = useState(false)

  // Permissions
  const { hasPermission } = usePermissions()
  const canEdit = hasPermission('config:edit')

  // Debounce search to avoid too many API calls
  const debouncedSearch = useDebounce(search, 300)

  // Build query params with pagination
  const queryParams = useMemo(() => ({
    search: debouncedSearch || undefined,
    areaPath: areaPath || undefined,
    ownerTeam: ownerTeam || undefined,
    country: country || undefined,
    limit: pageSize,
    offset: (page - 1) * pageSize,
  }), [debouncedSearch, areaPath, ownerTeam, country, page, pageSize])

  // Fetch service catalog
  const {
    data: catalogData,
    isLoading,
    error: fetchError,
  } = useServiceCatalog(queryParams)

  // Handle service row click
  const handleServiceClick = useCallback((service: ServiceSummary) => {
    setSelectedService(service.name)
  }, [])

  // Handle drawer close
  const handleDrawerClose = useCallback(() => {
    setSelectedService(null)
  }, [])

  // Update URL when filters change (and reset pagination)
  const updateUrlParams = useCallback((overrides: Record<string, string | undefined>) => {
    const newParams = new URLSearchParams()
    
    const newSearch = overrides.search ?? search
    const newAreaPath = overrides.areaPath ?? areaPath
    const newOwnerTeam = overrides.ownerTeam ?? ownerTeam
    const newCountry = overrides.country ?? country
    const newPage = overrides.page ?? (overrides.resetPage ? '1' : String(page))
    const newPageSize = overrides.pageSize ?? String(pageSize)
    
    if (newSearch) newParams.set('search', newSearch)
    if (newAreaPath) newParams.set('areaPath', newAreaPath)
    if (newOwnerTeam) newParams.set('ownerTeam', newOwnerTeam)
    if (newCountry) newParams.set('country', newCountry)
    if (newPage !== '1') newParams.set('page', newPage)
    if (newPageSize !== '25') newParams.set('pageSize', newPageSize)
    
    setSearchParams(newParams)
  }, [search, areaPath, ownerTeam, country, page, pageSize, setSearchParams])

  // Filter change handlers (reset pagination on filter change)
  const handleSearchChange = useCallback((value: string) => {
    setSearch(value)
    updateUrlParams({ search: value || undefined, resetPage: 'true' })
  }, [updateUrlParams])

  const handleAreaPathChange = useCallback((value: string) => {
    setAreaPath(value)
    updateUrlParams({ areaPath: value || undefined, resetPage: 'true' })
  }, [updateUrlParams])

  const handleOwnerTeamChange = useCallback((value: string) => {
    setOwnerTeam(value)
    updateUrlParams({ ownerTeam: value || undefined, resetPage: 'true' })
  }, [updateUrlParams])

  const handleCountryChange = useCallback((value: string) => {
    setCountry(value)
    updateUrlParams({ country: value || undefined, resetPage: 'true' })
  }, [updateUrlParams])

  // Pagination handlers
  const handlePageChange = useCallback((newPage: number) => {
    updateUrlParams({ page: String(newPage) })
  }, [updateUrlParams])

  const handlePageSizeChange = useCallback((newPageSize: number) => {
    updateUrlParams({ pageSize: String(newPageSize), page: '1' })
  }, [updateUrlParams])

  return (
    <PageShell>
      <div className="p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <PageHeader
            title="Service Catalog"
            description="Browse and search services across the organization"
          />
          {canEdit && (
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => setShowUploadDialog(true)}>
                <Upload className="h-4 w-4 mr-2" />
                Upload JSON
              </Button>
              <Button onClick={() => setShowAddDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Add Service
              </Button>
            </div>
          )}
        </div>

        {/* Error Alert */}
        {fetchError && (
          <Alert variant="destructive">
            <AlertTitle>Error</AlertTitle>
            <AlertDescription>
              Unable to load service catalog. Please try again later.
            </AlertDescription>
          </Alert>
        )}

        {/* Filters */}
        <ServiceCatalogFilters
          search={search}
          onSearchChange={handleSearchChange}
          areaPath={areaPath}
          onAreaPathChange={handleAreaPathChange}
          ownerTeam={ownerTeam}
          onOwnerTeamChange={handleOwnerTeamChange}
          country={country}
          onCountryChange={handleCountryChange}
          availableFilters={catalogData?.availableFilters}
        />

        {/* Table */}
        <Card className="overflow-hidden">
          <ServiceCatalogTable
            services={catalogData?.services ?? []}
            isLoading={isLoading}
            onServiceClick={handleServiceClick}
          />
          
          {/* Pagination */}
          {!isLoading && catalogData && catalogData.total > 0 && (
            <DataTablePagination
              page={page}
              pageSize={pageSize}
              total={catalogData.total}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
              pageSizeOptions={PAGE_SIZES}
              itemName="services"
            />
          )}
        </Card>

        {/* Empty State */}
        {!isLoading && catalogData?.services.length === 0 && !fetchError && (
          <EmptyState
            icon={Server}
            title="No services found"
            description={
              search || areaPath || ownerTeam || country
                ? 'Try adjusting your search or filters.'
                : 'No services have been added to the catalog yet.'
            }
          />
        )}

        {/* Service Detail Dialog */}
        <ServiceDetailDialog
          serviceName={selectedService}
          onClose={handleDrawerClose}
        />

        {/* Admin Dialogs */}
        <ServiceFormDialog
          open={showAddDialog}
          onOpenChange={setShowAddDialog}
          mode="add"
        />
        <BulkUploadDialog
          open={showUploadDialog}
          onOpenChange={setShowUploadDialog}
        />
      </div>
    </PageShell>
  )
}
